import styled from "styled-components";
import { Fragment } from "react";
import { usePage } from "@inertiajs/react";
import { Play } from "lucide-react";

import SkipLink from "../_kit/SkipLink";
import Media from "../_kit/Media";
import Header from "../home/Header";
import Footer from "../home/Footer";
import Button from "../_kit/Button";
import {
  Section,
  SectionHeader,
  Lede,
  Heading,
  Eyebrow,
  Container,
} from "../_kit/Section";
import {
  useLandingLanguage,
  LandingLanguageProvider,
} from "../../../contexts/landing-language";
import {
  weight,
  type,
  space,
  radius,
  motion,
  media,
  leading,
  color,
} from "../../../styles/tokens";

/* "/hebe/stories" — Instagram and TikTok in our own markup.
 *
 * Deliberately NOT an embed wall. Platform embeds drag in third-party script, cookies and their
 * chrome, and they cannot be made to match anything. Each post is a still we hold plus a link out,
 * so the grid is ours and the platform gets the click.
 *
 * Reached only when AppSetting `hebe.stories_enabled` is on; the route 404s otherwise.
 */

const ACCOUNTS = {
  instagram: {
    handle: "@hebehost",
    url: "https://www.instagram.com/hebehost/",
  },
  tiktok: { handle: "@hebe.host", url: "https://www.tiktok.com/@hebe.host" },
};

/* A masonry-ish grid: social stills are a mix of 4:5 portrait and 9:16 video, and forcing them all
 * to one square crops the subject out of half of them. */
const Wall = styled.div`
  columns: 4 260px;
  column-gap: ${space[4]};

  ${media.md`
    column-gap: ${space[3]};
  `}
`;

const Post = styled.a`
  position: relative;
  display: block;
  break-inside: avoid;
  margin-bottom: ${space[4]};
  border-radius: ${radius.md};
  overflow: hidden;
  background: ${color.canvasAlt};

  > div {
    transition: transform ${motion.slow};
  }

  &:hover > div {
    transform: scale(1.03);
  }
`;

const Overlay = styled.span`
  position: absolute;
  inset: auto 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 40px 14px 14px;
  background: ${color.scrimSoft};
  color: ${color.onDark};
  font-size: ${type.xs};
  line-height: ${leading.normal};
  opacity: 0;
  transition: opacity ${motion.base};

  ${Post}:hover & {
    opacity: 1;
  }

  /* Touch has no hover, so the caption is always legible there rather than unreachable. */
  @media (hover: none) {
    opacity: 1;
  }
`;

const Handle = styled.span`
  font-weight: ${weight.semibold};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const VideoBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  background: rgba(20, 21, 15, 0.55);
  color: ${color.onDark};
  backdrop-filter: blur(4px);
`;

const Follow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[3]};
  margin-top: ${space[7]};
`;

const Empty = styled.p`
  margin: 0;
  padding: ${space[8]} 0;
  text-align: center;
  color: ${color.ink500};
`;

const StoriesContent = () => {
  const { posts } = usePage().props;
  const { t } = useLandingLanguage();
  const copy = t.stories;

  return (
    <Fragment>
      <SkipLink href="#main">{t.hero.scrollHint}</SkipLink>
      <Header forceScrolled />
      <main id="main">
        <Section>
          <Container $width="wide">
            <SectionHeader>
              <Eyebrow>{copy.eyebrow}</Eyebrow>
              <Heading>{copy.heading}</Heading>
              <Lede>{copy.body}</Lede>
            </SectionHeader>

            {(posts || []).length === 0 ? (
              <Empty>{copy.empty}</Empty>
            ) : (
              <Wall>
                {posts.map((post) => (
                  <Post
                    key={post.permalink}
                    href={post.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={
                      post.platform === "tiktok" ? copy.watchOn : copy.viewOn
                    }
                  >
                    <Media
                      source={post.media}
                      alt={post.caption || ""}
                      sizes="(max-width: 768px) 50vw, 25vw"
                    />
                    {post.video ? (
                      <VideoBadge aria-hidden="true">
                        <Play size={13} />
                      </VideoBadge>
                    ) : null}
                    <Overlay>
                      <Handle>{ACCOUNTS[post.platform]?.handle}</Handle>
                      {post.caption ? (
                        <span>{post.caption.slice(0, 120)}</span>
                      ) : null}
                    </Overlay>
                  </Post>
                ))}
              </Wall>
            )}

            <Follow>
              <Button
                as="a"
                href={ACCOUNTS.instagram.url}
                target="_blank"
                rel="noopener noreferrer"
                $variant="secondary"
              >
                Instagram {ACCOUNTS.instagram.handle}
              </Button>
              <Button
                as="a"
                href={ACCOUNTS.tiktok.url}
                target="_blank"
                rel="noopener noreferrer"
                $variant="secondary"
              >
                TikTok {ACCOUNTS.tiktok.handle}
              </Button>
            </Follow>
          </Container>
        </Section>
      </main>
      <Footer />
    </Fragment>
  );
};

const StoriesPage = () => (
  <LandingLanguageProvider>
    <StoriesContent />
  </LandingLanguageProvider>
);

export default StoriesPage;
